import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import { FormattedMessage, injectIntl } from 'react-intl';

import { Box, Button, CircularProgress, Grid, IconButton, InputLabel, TextField, Tooltip } from '@material-ui/core';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import DeleteIcon from '@material-ui/icons/Delete';

import notification from 'app/config/notification';
import DialogComponent from 'app/components/DialogComponent';
import FileField from 'app/components/FileField';
import CustomTextField from '../../../../../Lines/components/TextField';

import companyEndpoints from 'app/api/services/companyEndpoints';
import CustomDateField from '../../../../../Lines/components/DateField';
import { SERVICES_GROUPS } from '../../../../../../config/services';

const emptyCertificationInitialValue = {
    nuDocumentoExtra: '',
    cdTipoDocumento: '',
    deEmpresa: '',
    dtEmissaoCrc: new Date(),
    dtVencimentoCrc: new Date(),
    deObservacao: '',
    cdLinhaDocumentoExtra: '',
};

function Modal(props) {
    const [loadingCompany, setLoadingCompany] = useState(false);
    const [sending, setSending] = useState(false);
    const [company, setCompany] = useState({});
    const [deleted, setDeleted] = useState(false);

    const filesStore = useSelector((state) => state.forms.files);
    const [isStrip, setIsStrip] = useState(false);

    const dispatch = useDispatch();

    const {
        classes,
        open,
        onClose,
        certification,
        request,
        onSave,
        downloadFile,
        updateFile,
        setUpdateFile,
        documentTypes,
        lines,
    } = props;

    const [certificationInitialValue, setCertificationInitialValue] = useState(emptyCertificationInitialValue);

    useEffect(() => {
        (async () => {
            if (open) {
                let cdLinha = '';
                if (request && request.jsonSolicitacao) {
                    const requestJsonData = JSON.parse(request.jsonSolicitacao);
                    if (requestJsonData.cdLinha) {
                        cdLinha = requestJsonData.cdLinha;
                    }
                }
                if (SERVICES_GROUPS.LINES_STRIP.includes(request.cdServico)) {
                    setIsStrip(true);
                }
                if (certification) {
                    let initial = { ...certificationInitialValue, ...certification };
                    if (!initial.cdLinhaDocumentoExtra) {
                        initial.cdLinhaDocumentoExtra = cdLinha;
                    }

                    if (
                        isGeratedOnLineManager &&
                        (initial.nuDocumentoExtra === null || initial.nuDocumentoExtra === '')
                    ) {
                        initial.nuDocumentoExtra = certification.cdCertificado;
                    }
                    setCertificationInitialValue(initial);
                }
                if (request.cdEmpresa) {
                    setLoadingCompany(true);
                    const response = await companyEndpoints.getCompany({ cdEmpresa: request.cdEmpresa });
                    if (response?.status === 200 && response?.data) {
                        setCompany(response.data);
                    }
                    setLoadingCompany(false);
                }
            } else {
                setCertificationInitialValue(emptyCertificationInitialValue);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const isGeratedOnLineManager = useMemo(() => {
        if (!certification) {
            return false;
        }
        return (
            certification.cdLinhaQuadroHorario ||
            certification.cdCondutorSubstituto ||
            certification.cdCepe ||
            certification.cdLinhaApostila ||
            certification.cdContratoTermoAditivo
        );
    }, [certification]);

    const onCancel = () => {
        setUpdateFile(false);
        onClose();
    };

    const deleteFile = (values) => {
        setUpdateFile(true);
        setDeleted(true);
        values.updateFile = true;
    };

    const handleSave = async (values) => {
        setSending(true);

        if (deleted && filesStore.length === 0) {
            notification('O envio do arquivo é obrigatório', {
                type: 'error',
            });
            setSending(false);
            return;
        }
        if (isGeratedOnLineManager) {
            values.nuDocumentoExtra = certification.nuDocumentoExtra;
        }

        await onSave(values);
        setDeleted(false);
        setSending(false);
    };

    const validationSchema = Yup.object().shape({
        cdTipoDocumento: Yup.string().required('Campo obrigatório').nullable(),
        dtEmissaoCrc: Yup.date().required('Campo obrigatório').typeError('Data inválida').nullable(),
        dtVencimentoCrc: Yup.date().required('Campo obrigatório').typeError('Data inválida').nullable(),
    });

    return (
        <DialogComponent title={'Detalhamento da documentação'} open={open} onClose={onClose}>
            <Formik
                enableReinitialize={true}
                initialValues={certificationInitialValue}
                validateOnBlur={false}
                validateOnChange={false}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    handleSave(values);
                }}>
                {({ values, validateForm }) => {
                    return (
                        <Form className={classes.containerForm}>
                            <Grid container spacing={1} className={classes.fieldsContainer}>
                                <Grid item xs={12} sm={!isStrip ? 3 : 2}>
                                    <InputLabel>Número</InputLabel>
                                    <Field
                                        name="nuDocumentoExtra"
                                        variant="filled"
                                        margin="dense"
                                        disabled={isGeratedOnLineManager}
                                        fullWidth
                                        as={TextField}
                                        inputProps={{
                                            maxLength: 30,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={!isStrip ? 6 : 5}>
                                    <InputLabel>Tipo de documento</InputLabel>
                                    <Field
                                        component={CustomTextField}
                                        name="cdTipoDocumento"
                                        variant="filled"
                                        margin="dense"
                                        disabled={isGeratedOnLineManager}
                                        select
                                        SelectProps={{
                                            native: true,
                                        }}>
                                        <option aria-label="None" value="">
                                            Selecione
                                        </option>
                                        {documentTypes?.map((type, index) => {
                                            return (
                                                <option key={`type-${index}`} value={type.cdTipoDocumento}>
                                                    {type.deTipoDocumento}
                                                </option>
                                            );
                                        })}
                                    </Field>
                                </Grid>
                                {isStrip && (
                                    <>
                                        <Grid item xs={12} sm={5}>
                                            {loadingCompany && (
                                                <div className={classes.progress}>
                                                    <CircularProgress size={20} />
                                                </div>
                                            )}
                                            {!loadingCompany && (
                                                <>
                                                    <InputLabel>Linha / Serviço</InputLabel>
                                                    <Field
                                                        component={CustomTextField}
                                                        name="cdLinhaDocumentoExtra"
                                                        variant="filled"
                                                        margin="dense"
                                                        select
                                                        SelectProps={{
                                                            native: true,
                                                        }}>
                                                        <option aria-label="None" value="">
                                                            Selecione
                                                        </option>
                                                        {lines?.map((line, index) => {
                                                            return (
                                                                <option key={`type-${index}`} value={line.cdLinha}>
                                                                    {line.deDescricao}
                                                                </option>
                                                            );
                                                        })}
                                                    </Field>
                                                </>
                                            )}
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                            <Grid container spacing={1} className={classes.fieldsContainer}>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Empresa</InputLabel>
                                    {loadingCompany && (
                                        <div className={classes.progress}>
                                            <CircularProgress size={20} />
                                        </div>
                                    )}
                                    {!loadingCompany && (
                                        <Field
                                            name="deEmpresa"
                                            disabled
                                            variant="filled"
                                            margin="dense"
                                            fullWidth
                                            value={
                                                company.cdTipoPessoaFisica ? company.nmCompleto : company.deRazaoSocial
                                            }
                                            as={TextField}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Emissão</InputLabel>
                                    <Field
                                        component={CustomDateField}
                                        label=""
                                        name="dtEmissaoCrc"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        disabled={isGeratedOnLineManager}
                                        autoOk
                                        clearable
                                        clearLabel={'Limpar'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <InputLabel>Vencimento</InputLabel>
                                    <Field
                                        component={CustomDateField}
                                        label=""
                                        name="dtVencimentoCrc"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        disabled={isGeratedOnLineManager}
                                        autoOk
                                        clearable
                                        clearLabel={'Limpar'}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <InputLabel>Observações</InputLabel>
                                    <Field
                                        name="deObservacao"
                                        variant="filled"
                                        margin="dense"
                                        multiline
                                        value={values.deObservacao ? values.deObservacao : ''}
                                        minRows={4}
                                        disabled={isGeratedOnLineManager}
                                        fullWidth
                                        as={TextField}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                                {!isGeratedOnLineManager && (
                                    <>
                                        {values.idFileCrc && !updateFile && (
                                            <>
                                                <Grid item xs={12} className={classes.containerIcons}>
                                                    <Tooltip title="Excluir arquivo atual e gerar novo">
                                                        <IconButton
                                                            className={classes.btnDeleteFile}
                                                            size="small"
                                                            onClick={() => deleteFile(values)}>
                                                            <DeleteIcon className={classes.iconDelete} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Button
                                                        color="primary"
                                                        className={classes.btnActualFile}
                                                        component="span"
                                                        disableElevation
                                                        onClick={async () =>
                                                            await downloadFile({ idFile: values.idFileCrc, dispatch })
                                                        }>
                                                        <InsertDriveFileIcon
                                                            className={classes.icon}
                                                            fontSize="inherit"
                                                        />{' '}
                                                        Ver arquivo atual
                                                    </Button>
                                                </Grid>
                                            </>
                                        )}
                                        {(!values.idFileCrc || updateFile) && (
                                            <Grid item xs={12} sm={6} md={6}>
                                                <Field
                                                    value={{}}
                                                    required={true}
                                                    name={`doc-1`}
                                                    label={'Carregar arquivo'}
                                                    id={`doc-1`}
                                                    component={FileField}
                                                />
                                            </Grid>
                                        )}
                                    </>
                                )}
                            </Grid>
                            <Grid container className={classes.actionsContainer}>
                                {sending && (
                                    <Grid item xs={12} className={classes.centerProgress}>
                                        <CircularProgress size={20} />
                                    </Grid>
                                )}
                                {!sending && (
                                    <>
                                        <Grid item xs={12} sm={4}>
                                            <Button className={classes.actionsBtns} onClick={() => onCancel()}>
                                                <FormattedMessage id="LABELS.COMMONS.CANCEL" />
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm={4} className={classes.textCenter}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className={classes.actionsBtns}
                                                onClick={() => {
                                                    validateForm().then(async (res) => {
                                                        if (Object.keys(res).length <= 0) {
                                                            handleSave(values);
                                                        } else {
                                                            notification(
                                                                'O(s) campo(s) sinalizado(s) precisam de revisão.',
                                                                {
                                                                    toastId: 'VALIDATION',
                                                                    type: 'error',
                                                                },
                                                            );
                                                        }
                                                    });
                                                }}>
                                                <Box className={classes.colorWhite}>Salvar</Box>
                                            </Button>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Form>
                    );
                }}
            </Formik>
        </DialogComponent>
    );
}

export default injectIntl(Modal);
